/**
 * P1-15: WLogin / WLoginForm / WCloudSaves / WCloudSaveRecord 迁移。
 *
 * 只迁 UI 与交互；鉴权网络流原样调用旧全局与引擎方法（不重写）：
 * - 登录 POST：`$.ajax` → game.server.getServerUrl() + "/user/login/"（toolbar.jsx.js:411-440）
 * - gravatar：全局 `md5`（lib/md5.js）
 * - 云存档：game.server.pushSave/loadSave/syncSaveData/pushSaveMetadata、
 *   game.ui.confirm、game.telemetry.guid、game.lastBackup
 *
 * 注：输入框对 keyup 停传播，避免打字触发全局快捷键（js/ui.js:182 的 html onkeyup；
 * 旧版注释即此意图，toolbar.jsx.js:382）。
 */
import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import { IconStat } from "../../components/IconStat";

declare global {
  interface Window {
    md5?: (input: string) => string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $?: any;
  }
}

const stopKeyPropagation = (e: KeyboardEvent) => {
  e.stopPropagation();
  e.nativeEvent.stopImmediatePropagation();
};

// ---------------------------------------------------------------------------
// WLoginForm
// ---------------------------------------------------------------------------

function LoginForm({ game }: { game: GamePage }) {
  const [login, setLogin] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (isLoading) {
    return <span>Loading...</span>;
  }

  const userProfile = game.server.userProfile;
  if (userProfile) {
    return (
      <div className="userProfile">
        <img
          src={"https://www.gravatar.com/avatar/" + (userProfile.email && window.md5 ? window.md5(userProfile.email) : "n/a") + "?s=15"}
        />
        <a href="/ui/profile" target="_blank" rel="noreferrer">
          {userProfile.id}
        </a>
        <a
          className="link logout"
          href="#"
          onClick={(e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setError(null);
            setIsLoading(true);
            game.server.logout().always(() => {
              //reset the form so the login inputs come back empty
              setLogin(null);
              setPassword(null);
              setIsLoading(false);
            });
          }}
        >
          {t("ui.kgnet.logout")}
        </a>
      </div>
    );
  }

  const doLogin = () => {
    setError(null);
    setIsLoading(true);
    window.$.ajax({
      cache: false,
      type: "POST",
      dataType: "json",
      data: {
        email: login,
        password: password,
      },
      xhrFields: {
        withCredentials: true,
      },
      url: game.server.getServerUrl() + "/user/login/",
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).done((resp: any) => {
      if (resp.id) {
        game.server.setUserProfile(resp);
      } else {
        setError(resp.error);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }).fail((resp: any, status: string) => {
      console.error("something went wrong, resp:", resp, status);
      setError(resp.responseText || "There was a problem connecting the server");
    }).always(() => {
      setIsLoading(false);
    });
  };

  return (
    <span onClick={(e: MouseEvent) => e.stopPropagation()}>
      <div className="row">
        <label htmlFor="kgnet-email">Email:</label>
        <input
          id="kgnet-email"
          type="email"
          onChange={(e) => setLogin(e.target.value)}
          onKeyUp={stopKeyPropagation}
          value={login ?? ""}
        />
        <label htmlFor="kgnet-password">Password:</label>
        <input
          id="kgnet-password"
          type="password"
          onChange={(e) => setPassword(e.target.value)}
          onKeyUp={stopKeyPropagation}
          value={password ?? ""}
        />
      </div>
      <div className="row">
        <a
          href="#"
          onClick={(e: MouseEvent) => {
            e.preventDefault();
            doLogin();
          }}
        >
          login
        </a>
        <a target="_blank" href="http://kittensgame.com/ui/register" rel="noreferrer">
          register
        </a>
      </div>
      {error && (
        <div className="row">
          <span className="error">{error}</span>
        </div>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// WCloudSaveRecord / WCloudSaves
// ---------------------------------------------------------------------------

interface CloudSave {
  guid: string;
  label?: string;
  archived?: boolean;
  size: number;
  timestamp: number;
  index?: { calendar: { year: number; day: number } };
}

function bytesToSize(bytes: number): string {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes == 0) {
    return "0 Byte";
  }
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i)) + " " + sizes[i];
}

function CloudSaveRecord({ game, save, closeMenu }: { game: GamePage; save: CloudSave; closeMenu?: () => void }) {
  const [showActions, setShowActions] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [label, setLabel] = useState(save.label);

  const isActiveSave = save.guid == game.telemetry.guid;
  const guid = save.guid;

  const stop = (e: MouseEvent) => e.stopPropagation();

  return (
    <div className={"save-record " + (save.archived ? "archived" : "")}>
      <div className="save-record-cell">
        {isEditable ? (
          <input
            onClick={stop}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e: KeyboardEvent) => {
              //TODO: set save label
              if (e.key === "Enter") {
                game.server.pushSaveMetadata(save.guid, { label }).then(() => {
                  //force sync-up of the game's server state with UI
                  //(pushMetadata should return a new save snapshot)
                  setLabel(label);
                });
                setIsEditable(false);
              }
            }}
          />
        ) : (
          <a
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              setIsEditable(!isEditable);
            }}
          >
            {save.label || guid.substring(guid.length - 4, guid.length)}
          </a>
        )}
        {isActiveSave ? "[" + t("ui.kgnet.save.current") + "]" : ""}
      </div>
      <div className="save-record-cell">
        {save.index ? "Year " + save.index.calendar.year + ", day " + save.index.calendar.day : "loading..."}
      </div>
      <div className="save-record-cell">
        {new Date(save.timestamp).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          hourCycle: "h24",
        })}
      </div>
      <div className="save-record-cell">{bytesToSize(save.size)}</div>
      {isActiveSave && (
        <a
          className="link"
          title="Upload your current game save to the server (this will owerwrite your old cloud save)"
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            game.ui.confirm("[S]ave", "This will override [SERVER] save. Y/N", () => {
              game.server.pushSave();
              closeMenu?.();
            });
          }}
        >
          {t("ui.kgnet.save.save")}
        </a>
      )}
      <a
        className="link"
        title="Download a cloud save and apply it to your game (your current data will be lost)"
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          game.ui.confirm("[L]oad", "This will override [LOCAL] save. Y/N", () => {
            game.server.loadSave(save.guid);
            closeMenu?.();
          });
        }}
      >
        {t("ui.kgnet.save.load")}
      </a>
      <a
        className="link"
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          setShowActions(!showActions);
        }}
      >
        ..
      </a>
      {showActions && (
        <a
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            setIsEditable(!isEditable);
          }}
        >
          edit
        </a>
      )}
      {showActions && (
        <a
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            game.server.pushSaveMetadata(save.guid, { archived: !save.archived }).then(() => {
              //force sync-up of the game's server state with UI
              setShowActions(showActions);
            });
          }}
        >
          archive
        </a>
      )}
    </div>
  );
}

function CloudSaves({ game, closeMenu }: { game: GamePage; closeMenu?: () => void }) {
  const [isLoading, setIsLoading] = useState(false);

  if (!game.server.userProfile) {
    return null;
  }

  const saveData = game.server.saveData as CloudSave[] | undefined;
  let hasActiveSaves = false;
  if (saveData && saveData.length) {
    for (const i in saveData) {
      if (saveData[i].guid == game.telemetry.guid) {
        hasActiveSaves = true;
      }
    }
  }

  return (
    <div>
      <div className="save-record-container">
        {saveData && (
          <div className="save-record header">
            <div className="save-record-cell">Id</div>
            <div className="save-record-cell">Save</div>
            <div className="save-record-cell">Last update</div>
            <div className="save-record-cell">Size</div>
            <div className="save-record-cell">Actions</div>
          </div>
        )}
        {saveData &&
          saveData.map((save) => (
            <CloudSaveRecord key={save.guid} save={save} game={game} closeMenu={closeMenu} />
          ))}
      </div>

      <div className="save-record-container">
        {saveData && !hasActiveSaves && (
          <div className="save-record">
            <a
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                game.server.pushSave();
                closeMenu?.();
              }}
            >
              Create new save ({game.telemetry.guid})
            </a>
          </div>
        )}
        <div className="save-record">
          <a
            className="link"
            title="Fetch the latest information about your cloud saves from the serer. This is a safe operation and it wont change any data."
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              setIsLoading(true);
              game.server.syncSaveData().always(() => {
                setIsLoading(false);
              });
            }}
          >
            {isLoading && "[loading..]"}
            {t("ui.kgnet.sync")}
          </a>
          {(!saveData || !saveData.length) && (
            <span style={{ paddingTop: "10px" }}>{t("ui.kgnet.instructional")}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WLogin
// ---------------------------------------------------------------------------

export function ToolbarLogin() {
  const [isExpanded, setIsExpanded] = useState(false);

  const online = useGameTick((game) => (game ? !!game.server.userProfile : null));
  // 保留 1 位小数天数（旧版每 tick 重算；此处仅在 0.1 天粒度变化时重渲染）
  const lastBackupDays = useGameTick((game) =>
    game ? Math.round(((Date.now() - game.lastBackup) / (1000 * 60 * 60 * 24)) * 10) / 10 : null,
  );

  if (online === null || lastBackupDays === null) {
    return null;
  }
  const game = getGame() as GamePage;
  const staleBackup = lastBackupDays >= 7;

  let popup: ReactNode = null;
  if (isExpanded) {
    popup = (
      <div className="login-popup button_tooltip tooltip-block">
        <div>
          <div className="last-backup">
            {staleBackup && <span className="hazard" />}
            Last backup: {lastBackupDays.toFixed(1) + " days ago"}
            {staleBackup && <span className="hazard" />}
          </div>
          <LoginForm game={game} />
          <CloudSaves game={game} closeMenu={() => setIsExpanded(false)} />
        </div>
      </div>
    );
  }

  return (
    <IconStat>
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === "Escape") {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <a
          className="kgnet-login-link-container"
          href="#!"
          onClick={(e: MouseEvent) => {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }}
        >
          <span
            className={
              "kgnet-login-link status-indicator-" + (online ? "online" : "offline") +
              (staleBackup ? " freshMessage" : "")
            }
          >
            <div className="svg-icon user" />
            {online ? t("ui.kgnet.online") : t("ui.kgnet.login")}
          </span>
        </a>
        {popup}
      </div>
    </IconStat>
  );
}
